import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faTimes } from '@fortawesome/free-solid-svg-icons';
import './InvoiceValidationModal.css';

/**
 * Styled validation warning modal for invoicing module
 * Replaces basic alert() with a professional, styled modal matching CRM module design
 * Now includes field highlighting functionality
 * 
 * IMPORTANT: Error highlighting persists after modal closes until field is fixed
 * Use clearFieldError() utility function to remove highlighting when field is updated
 */
const InvoiceValidationModal = ({ 
  isOpen, 
  onClose, 
  title = "Validation Required",
  errors = [],
  fieldIds = [] // Array of field IDs that are missing values
}) => {
  useEffect(() => {
    if (isOpen && fieldIds.length > 0) {
      // Add error class to all missing fields
      fieldIds.forEach(fieldId => {
        const element = document.getElementById(fieldId);
        if (element) {
          element.classList.add('invoice-field-error');
          // Also add to parent for form-field styling
          const parent = element.closest('.form-field, .form-field-flexible, .vessel-field, .line-item-cell');
          if (parent) {
            parent.classList.add('invoice-field-has-error');
          }
        }
      });
      
      // Scroll to first missing field
      if (fieldIds[0]) {
        const firstElement = document.getElementById(fieldIds[0]);
        if (firstElement) {
          setTimeout(() => {
            firstElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 300);
        }
      }
    }
    // NOTE: No cleanup function - errors persist until fields are fixed
  }, [isOpen, fieldIds]);
  
  if (!isOpen) return null;
  
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  
  return (
    <div className="invoice-validation-modal-overlay" onClick={handleOverlayClick}>
      <div className="invoice-validation-modal">
        {/* Header */}
        <div className="invoice-validation-modal-header">
          <div className="invoice-validation-modal-header-content">
            <div className="invoice-validation-modal-icon">
              <FontAwesomeIcon icon={faExclamationTriangle} />
            </div>
            <h3>{title}</h3>
          </div>
          <button 
            className="invoice-validation-close-btn" 
            onClick={onClose}
            type="button"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        {/* Body */}
        <div className="invoice-validation-modal-body">
          <p className="invoice-validation-message">
            Please complete the following required fields before proceeding:
          </p>
          
          <div className="invoice-validation-errors-box">
            <ul>
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        </div>
        
        {/* Footer */}
        <div className="invoice-validation-modal-footer">
          <button 
            className="invoice-validation-btn-close" 
            onClick={onClose}
            type="button"
          >
            OK, I'll Complete These Fields
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Utility function to clear field error highlighting when user fixes a field
 * Call this in onChange handlers to remove red highlighting
 * 
 * @param {string|string[]} fieldId - Single field ID or array of field IDs to clear
 */
export const clearFieldError = (fieldId) => {
  const fieldIds = Array.isArray(fieldId) ? fieldId : [fieldId];
  
  fieldIds.forEach(id => {
    const element = document.getElementById(id);
    if (element) {
      element.classList.remove('invoice-field-error');
      const parent = element.closest('.form-field, .form-field-flexible, .vessel-field, .line-item-cell');
      if (parent) {
        parent.classList.remove('invoice-field-has-error');
      }
    }
  });
};

/**
 * Utility function to clear all field error highlighting
 * Useful when resetting a form or navigating away
 */
export const clearAllFieldErrors = () => {
  // Remove all error classes from elements
  document.querySelectorAll('.invoice-field-error').forEach(el => {
    el.classList.remove('invoice-field-error');
  });
  
  document.querySelectorAll('.invoice-field-has-error').forEach(el => {
    el.classList.remove('invoice-field-has-error');
  });
};

export default InvoiceValidationModal;

